import { en } from './translations/en';
import { ptBR } from './translations/pt-br';
import { reportWarning } from '@/lib/reporting';

export type Language = 'en' | 'pt-br';

// Define the shape based on en, but allow any string values
type DeepStringRecord<T> = {
  [K in keyof T]: T[K] extends Record<string, unknown> 
    ? DeepStringRecord<T[K]> 
    : string;
};

export type Translations = DeepStringRecord<typeof en>;

export const translations: Record<Language, Translations> = {
  'en': en as Translations,
  'pt-br': ptBR as Translations,
};

export const STORAGE_KEY = 'coffeelog-language';
export const DEFAULT_LANGUAGE: Language = 'pt-br';

// Detect browser language preference
// Returns 'pt-br' for Portuguese speakers (Brazil), 'en' for everyone else
export function detectBrowserLanguage(): Language {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  
  const browserLangs = navigator.languages || [navigator.language];
  
  for (const lang of browserLangs) {
    const normalized = lang.toLowerCase();
    // Match pt-BR, pt-br, pt_BR, or just pt
    if (normalized.startsWith('pt')) {
      return 'pt-br';
    }
  }
  
  return 'en';
}

// Helper to get nested translation value
export function getTranslation(
  translationsObj: Translations,
  key: string,
  params?: Record<string, string | number>
): string {
  const keys = key.split('.');
  let value: unknown = translationsObj;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = (value as Record<string, unknown>)[k];
    } else {
      reportWarning('Translation key not found', { key });
      return key;
    }
  }
  
  if (typeof value !== 'string') {
    reportWarning('Translation value is not a string', { key });
    return key;
  }
  
  // Replace parameters like {name} with actual values
  if (params) {
    return value.replace(/\{(\w+)\}/g, (_, paramKey) => {
      return params[paramKey]?.toString() ?? `{${paramKey}}`;
    });
  }
  
  return value;
}

export { en, ptBR };
